import { createApi } from "@reduxjs/toolkit/query/react"

import { baseQueryWithAuth } from "utils/api"

export type OnlineStatusReportPeriod = "last_week" | "last_month"
export type OnlineStatusReportState = "preparing" | "complete" | "failed"

export interface OnlineStatusReportingPeriod {
  preset: OnlineStatusReportPeriod
  from: string
  to: string
  display_label: string
}

export interface OnlineStatusReportLifecycleResponse {
  state: OnlineStatusReportState
  report_id: string
  reused?: boolean
  retry_after_seconds?: number
  reporting_period?: OnlineStatusReportingPeriod
  error?: {
    code: string
    message: string
  }
}

export interface OnlineStatusReportSummary extends OnlineStatusReportLifecycleResponse {
  overall_online_percentage: number | null
  eligible_device_count: number
  reporting_period: OnlineStatusReportingPeriod
}

export interface OnlineStatusReportDevice {
  sn: string
  customer_email: string | null
  status: "installed" | "transferring"
  online_percentage: number | null
  address: string | null
  thermostat_name: string | null
}

export interface OnlineStatusReportDevices extends OnlineStatusReportLifecycleResponse {
  data: OnlineStatusReportDevice[]
  links: Record<string, string | null>
  meta: {
    current_page: number
    last_page: number
    from: number
    per_page: number
    to: number
    total: number
  }
}

export interface OnlineStatusReportDevicesQuery {
  reportId: string
  params: {
    sn?: string
    customer_email?: string
    address?: string
    thermostat_name?: string
    order_column?: string
    order_type?: "ASC" | "DESC"
    page?: number
    per_page?: number
  }
}

const onlineStatusReportPath = "/contractors/reports/online-status"

export const onlineStatusReportApi = createApi({
  reducerPath: "onlineStatusReport",
  baseQuery: baseQueryWithAuth,
  endpoints: builder => ({
    createOnlineStatusReport: builder.mutation<
      OnlineStatusReportLifecycleResponse,
      { period: OnlineStatusReportPeriod }
    >({
      query: body => ({
        url: onlineStatusReportPath,
        method: "POST",
        body,
      }),
    }),
    getOnlineStatusReportSummary: builder.query<OnlineStatusReportSummary, string>({
      query: reportId => `${onlineStatusReportPath}/${reportId}/summary`,
    }),
    getOnlineStatusReportDevices: builder.query<OnlineStatusReportDevices, OnlineStatusReportDevicesQuery>({
      query: ({ reportId, params }) => ({
        url: `${onlineStatusReportPath}/${reportId}/devices`,
        params,
      }),
    }),
  }),
})

export const {
  useCreateOnlineStatusReportMutation,
  useLazyGetOnlineStatusReportSummaryQuery,
  useLazyGetOnlineStatusReportDevicesQuery,
} = onlineStatusReportApi
